export type GalleryTheme = {
  fontFamily: string;
  background: string;
  foreground: string;
  accent: string;
  layout: "grid" | "masonry" | "scrapbook";
  imageFit: "cover" | "contain";
  density: "comfortable" | "compact";
  scrapbookPage: "landscape" | "portrait";
};

export type PhotoFrameStyle = "none" | "mat" | "instant" | "film" | "wood" | "scalloped" | "painted";

export type PhotoFrame = {
  style: PhotoFrameStyle;
  color: string;
  width: number;
};

export type ScrapbookPosition = {
  x: number;
  y: number;
  width: number;
  rotation: number;
  z: number;
};

export type ScrapbookPlacement = {
  desktop: ScrapbookPosition;
  mobile: ScrapbookPosition;
};

export type Gallery = {
  titleFont?: string | null;
  slug: string;
  displayName: string;
  theme: GalleryTheme;
  createdAt: string;
  isOwner?: boolean;
  photoCount?: number;
  coverPhotoUrl?: string | null;
  previewPhotoUrls?: string[];
};

export type Photo = {
  id: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  width: number | null;
  height: number | null;
  originalWidth: number | null;
  originalHeight: number | null;
  checksumSha256: string;
  sortOrder: number;
  isHidden: boolean;
  isCover: boolean;
  hasEdits: boolean;
  createdAt: string;
  mediaUrl: string;
  originalUrl: string;
  frame: PhotoFrame;
  scrapbook: ScrapbookPlacement | null;
};

export type CropArea = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type EditRecipe = {
  version: 1;
  aspect: number;
  rotation: number;
  flipHorizontal: boolean;
  flipVertical: boolean;
  exposure: number;
  contrast: number;
  saturation: number;
  croppedAreaPercentages: CropArea;
};

export const defaultTheme: GalleryTheme = {
  fontFamily: "system",
  background: "#f8f6f1",
  foreground: "#171717",
  accent: "#456554",
  layout: "grid",
  imageFit: "cover",
  density: "comfortable",
  scrapbookPage: "landscape"
};

export const fontOptions = [
  { label: "Sans", value: "system", css: "'DM Sans', ui-sans-serif, system-ui, sans-serif" },
  { label: "Serif", value: "serif", css: "Georgia, Cambria, Times New Roman, serif" },
  { label: "Mono", value: "mono", css: "SFMono-Regular, Consolas, Liberation Mono, monospace" },
  { label: "Rounded", value: "rounded", css: "ui-rounded, Avenir Next, Nunito, system-ui, sans-serif" }
];

export const themePresets: Array<{ name: string; theme: GalleryTheme }> = [
  { name: "Gallery", theme: defaultTheme },
  {
    name: "Editorial",
    theme: {
      fontFamily: "serif",
      background: "#f3f1eb",
      foreground: "#20211f",
      accent: "#9b3b32",
      layout: "masonry",
      imageFit: "contain",
      density: "comfortable",
      scrapbookPage: "portrait"
    }
  },
  {
    name: "Nocturne",
    theme: {
      fontFamily: "system",
      background: "#151719",
      foreground: "#f5f4ef",
      accent: "#e3b341",
      layout: "grid",
      imageFit: "contain",
      density: "compact",
      scrapbookPage: "landscape"
    }
  },
  {
    name: "Studio",
    theme: {
      fontFamily: "rounded",
      background: "#eef1ed",
      foreground: "#17201a",
      accent: "#2563a6",
      layout: "masonry",
      imageFit: "cover",
      density: "compact",
      scrapbookPage: "landscape"
    }
  }
];

export function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let size = bytes / 1024;
  let index = 0;
  while (size >= 1024 && index < units.length - 1) {
    size /= 1024;
    index += 1;
  }
  return `${size.toFixed(size >= 10 ? 0 : 1)} ${units[index]}`;
}

export async function getImageDimensions(file: File) {
  try {
    const bitmap = await createImageBitmap(file);
    const dimensions = { width: bitmap.width, height: bitmap.height };
    bitmap.close();
    return dimensions;
  } catch {
    return { width: null, height: null };
  }
}

export function photoRatio(photo: Photo) {
  if (!photo.width || !photo.height) return "4 / 3";
  return `${photo.width} / ${photo.height}`;
}

export const frameOptions: Array<{ style: PhotoFrameStyle; label: string; color: string }> = [
  { style: "none", label: "None", color: "#ffffff" },
  { style: "mat", label: "Gallery mat", color: "#f4efe6" },
  { style: "instant", label: "Instant", color: "#fffdf8" },
  { style: "film", label: "Film", color: "#171717" },
  { style: "wood", label: "Wood", color: "#8b5e3c" },
  { style: "scalloped", label: "Scalloped", color: "#d9d1ee" },
  { style: "painted", label: "Painted", color: "#f2aaa4" }
];

export function defaultPhotoFrame(index = 0): PhotoFrame {
  const palette = ["#f2aaa4", "#d9d1ee", "#f4c58d", "#b8d8c5", "#f1d785"];
  return { style: "painted", color: palette[index % palette.length], width: 12 };
}

export function defaultScrapbookPlacement(index: number): ScrapbookPlacement {
  const desktopColumns = [5, 37, 69];
  const desktopRow = Math.floor(index / desktopColumns.length) % 3;
  const mobileColumns = [5, 53];
  const mobileRow = Math.floor(index / mobileColumns.length) % 4;
  const rotations = [-3, 2, -1, 3, -2, 1];
  return {
    desktop: {
      x: desktopColumns[index % desktopColumns.length],
      y: 8 + desktopRow * 30,
      width: 26,
      rotation: rotations[index % rotations.length],
      z: index
    },
    mobile: {
      x: mobileColumns[index % mobileColumns.length],
      y: 6 + mobileRow * 22,
      width: 42,
      rotation: rotations[index % rotations.length],
      z: index
    }
  };
}

export function contrastRatio(first: string, second: string) {
  const luminance = (hex: string) => {
    const channels = [1, 3, 5].map((index) => Number.parseInt(hex.slice(index, index + 2), 16) / 255);
    const [red, green, blue] = channels.map((channel) =>
      channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4
    );
    return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
  };

  const light = Math.max(luminance(first), luminance(second));
  const dark = Math.min(luminance(first), luminance(second));
  return (light + 0.05) / (dark + 0.05);
}
